import * as d3 from 'd3-selection'
import { nest } from 'd3-collection'
import * as axis from 'd3-axis'
import * as array from 'd3-array'
import * as colors from 'colors'

const yAxisLabelHeight = 45

// This matches the d3 default axis.tickPadding, we tweak this
// based on the RTL
const tickPadding = 3

/*
 * This is used when an axis has a `discreteTicks` array which should be used
 * to control the tick marks
 */
function getDiscreteTicks (orient, layout, scaler, tickCount) {
  let axis = layout.axes[orient]
    , ticks = axis.discreteTicks
    , visibility = new Map(ticks.map(tick => [tick, true])) // all discrete visible by default
    , domain = scaler.domain()
    , tickBuffer = 10

  // Remove any of the discrete ticks that are outside of our scale
  for (const [key] of visibility) {
    if (key < domain[0] || key > domain[1]) {
      visibility.delete(key) // Remove this tick entirely, not just hide its label
    }
  }

  // Build an array of the visibility ticks
  let visibles = Array.from(visibility.keys())
    , topTickLocation = scaler(visibles[visibles.length - 1])
    , bottomTickLocation = scaler(visibles[0])

  visibles.forEach((tick, i) => {
    if (i == 0 || i == visibles.length - 1) {
      // Top or bottom, always show
      return
    }

    // Check if the top of this tick is intersecting w/ the top tick
    if (scaler(tick) - tickBuffer < topTickLocation) {
      visibility.delete(tick)
      return
    }

    // See if this tick is overlapping w/ the bottom tick
    if (scaler(tick) + tickBuffer > bottomTickLocation) {
      // Yep, hide it.
      visibility.delete(tick)
      return
    }

    // Check if this tick is overlapping a visible tick below it
    let belows = Array.from(visibility.keys()).filter(key => {
        return visibility.get(key) && key < tick
      })
      , belowLocation = scaler(belows[belows.length - 1])

    if (scaler(tick) + tickBuffer > belowLocation) {
      visibility.delete(tick)
    }


  })

  return visibility
}

function getTicks (orient, layout, scaler, format, tickCount) {
  if (layout.axes[orient].discreteTicks) {
    return getDiscreteTicks(orient, layout, scaler, tickCount)
  }

  let scalerTicks = scaler.ticks(tickCount) // Ticks that the scaler would draw by default
    , visibility = scalerTicks.reduce((map, tick) => {
                    map.set(tick, false)
                    return map
                  }, new Map) // Use a es6 map over an object so the keys can be numbers.

  nest().key(tick => format(tick)) // Create a map key'd off the formatted label
        .entries(scalerTicks)
        .forEach(ticks => {
          if (ticks.values.length === 1) {
            // No duplicates, show this tick
            visibility.set(ticks.values[0], true)
            return
          }

          let minY = parseInt(scaler(+scaler._domain[0]))
            , maxY = parseInt(scaler(+scaler._domain[1]))

          // There are dups
          ticks.values.some(function (val, i) {
            var y = parseInt(scaler(val))

            if (y == minY || y == maxY) {
              // Always show the actual min/max if there is a label at that position
              visibility.set(val, true)
              return true
            }

            if (i === ticks.values.length - 1) {
              // This is the top tick, and we didn't find a tick to show yet
              visibility.set(val, true)
              return true
            }
          })
        })

  return visibility
}

function getTickData (scalers, layout, width) {
  if (!layout.hasYAxis) {
    return []
  }
  // Only sides that have a set scaler
  let sides = ['left', 'right'].filter(orient => scalers[orient])

  return sides.map(orient => {
                let scaler = scalers[orient]
                  , data = [scaler._domain]
                  , tickCount = Math.max(Math.floor(Math.abs(array.max(scaler.range()) / yAxisLabelHeight)), 2)
                  , formatter = layout.getFormatter(orient)
                  , ticks = getTicks(orient, layout, scaler, formatter, tickCount)
                  , minValue = layout.minValue(orient)

                data.orient = orient
                data.color = scaler.color
                data.axis = orient === 'left' ? axis.axisLeft(scaler) : axis.axisRight(scaler)

                data.axis.tickValues(Array.from(ticks.keys())) // All of them, so the horizontal lines are drawn
                         .tickFormat(tick => {
                           if (tick < 0 && minValue >= 0) {
                             // See https://github.com/SpiderStrategies/Scoreboard/issues/44611
                             // Hide negative labels if all charted values are 0 or positive
                             return ''
                           }
                           // Check if this tick is visible. If so, run it through the formatter. Otherwise, it will be an empty `text` element
                           return ticks.get(tick) ? formatter(tick) : ''
                         })

                data.axis.tickPadding(layout.rtl ? -tickPadding : tickPadding)
                if (orient === 'right') {
                  data.axis.tickSize(0, 0)
                  data.transform = `translate(${width})`
                } else{
                  data.axis.tickSize(-width, 0)
                }

                return data
              })
}

export default function (layout, width, scalers, transition) {
  var draw = function (selection) {
    selection.each(function (data) {
      let sides = d3.select(this)
                    .selectAll('g.axis')
                    .data(getTickData(scalers, layout, width))

      sides.exit().remove()

      let y = sides.enter()
                   .append('g')
                   .select(function (data) {
                     d3.select(this).call(data.axis)
                     return this
                   })
                   .merge(d3.select(this).selectAll('g.axis'))

      y.attr('class', d => {
          return `axis axis-y axis-y-${d.orient}`
       })
       .attr('data-orient', d => d.orient)
       .classed('no-labels', !(layout.axes.left.labels && layout.axes.right.labels))
       .attr('fill', null) // D3 sets a default. We don't want it. Same for attrs below
       .attr('font-size', null)
       .attr('font-family', null)
       .attr('transform', d => d.transform)

      y.select(function (data) {
         d3.select(this)
           .selectAll('text')
           .style('fill', data.color && colors.lookup(data.color)) // Only set the fill if we have a color

         d3.select(this)
           .transition(transition)
           .call(data.axis)
       })

      y.select('path.domain').remove()
    })
  }

  return draw
}
